import { documentType, libraryFileType } from './documentUpload.js'

// 后端接口封装。认证走 Authorization: Bearer <token>，令牌存 localStorage。
const TOKEN_KEY = 'etc_cs_token'
const AGENT_KEY = 'etc_cs_agent'

export const auth = {
  get token() { return localStorage.getItem(TOKEN_KEY) || '' },
  get agent() {
    try { return JSON.parse(localStorage.getItem(AGENT_KEY) || 'null') } catch { return null }
  },
  get isAdmin() { return this.agent?.role === 'admin' },
  get isAftersales() { return this.agent?.role === 'aftersales' },
  save(token, agent) {
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(AGENT_KEY, JSON.stringify(agent))
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(AGENT_KEY)
  },
}

// 后端把「会话没了 / 不在视野内」这类正常状态变化回 403 + 业务码（见 backend/api-errors.mjs）。
// 前端据此放手：清掉当前选中、停止继续轮询这通会话，而不是把它当故障一直重试。
export const LOST_CONVERSATION_CODES = ['NO_ACCESS', 'CONVERSATION_GONE', 'OWNER_RETURNED']
export const isConversationLost = e => LOST_CONVERSATION_CODES.includes(e?.code)

// 失权时给运营看的话，按原因分开：被删了和"转出去了"不是一回事
export const LOST_MESSAGES = {
  NO_ACCESS: '这通会话已转出或权限已变更，已从列表移除',
  CONVERSATION_GONE: '这通会话已不存在，已从列表移除',
}
// 交还这种要说清「谁接管了」，用后端带回来的原话；其余用固定文案
export const lostConvMessage = e => (e?.code === 'OWNER_RETURNED' && e?.message)
  ? e.message : (LOST_MESSAGES[e?.code] || LOST_MESSAGES.NO_ACCESS)

export class ApiError extends Error {
  constructor(message, { status, code } = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

async function request(path, options = {}) {
  const res = await fetch(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(auth.token ? { Authorization: `Bearer ${auth.token}` } : {}),
      ...options.headers,
    },
  })
  const data = await res.json().catch(() => ({}))
  if (res.status === 401) {
    auth.clear()
    if (!location.hash.includes('/login')) location.hash = '#/login'
    throw new ApiError(data.error || '登录已失效', { status: 401, code: data.code })
  }
  if (!res.ok) throw new ApiError(data.error || `请求失败 (${res.status})`, { status: res.status, code: data.code })
  return data
}

export async function login(agentId, password) {
  const d = await request('/api/cs/login', {
    method: 'POST', body: JSON.stringify({ agentId, password }),
  })
  auth.save(d.token, d.agent)
  return d.agent
}

export async function logout() {
  await request('/api/cs/logout', { method: 'POST' }).catch(() => {})
  auth.clear()
}

export function changePassword(oldPassword, newPassword) {
  return request('/api/cs/change-password', {
    method: 'POST', body: JSON.stringify({ oldPassword, newPassword }),
  })
}

// 统一的 BFF 调用
async function action(name, params = {}) {
  const d = await request('/api/cs/admin', {
    method: 'POST', body: JSON.stringify({ action: name, params }),
  })
  return d.data
}

export const api = {
  // 会话
  listConversations: (p) => action('listConversations', p),
  countConversations: (p) => action('countConversations', p),
  countUnread: () => action('countUnread'),
  listDispatchNotices: () => action('listDispatchNotices'),
  listFollowUps: (p) => action('listFollowUps', p),   // 跟进提醒（喇叭）
  getFollowUpReport: () => action('getFollowUpReport'),   // 跟进日报/周报，只有管理员
  // 长轮询：后端有写入就立刻返回新版本号，没有最多挂 25 秒。只给 lib/changes.js 用
  waitChanges: (since, presence = false) => action('waitChanges', { since, presence }),
  markRead: (convId) => action('markConversationRead', { convId }),
  getConversation: (id) => action('getConversation', { id }),
  listMessages: (convId, limit = 200, sinceCreatedAt) => action('listMessages', { convId, limit, sinceCreatedAt }),
  claim: (id, agentId) => action('claimConversation', { id, agentId }),
  assignConvs: (convIds, agentId, force) => action('assignConversations', { convIds, agentId, force }),
  distributeConvs: (convIds, agentIds, force) => action('distributeConversations', { convIds, agentIds, force }),
  release: (id) => action('releaseConversation', { id }),
  transfer: (id, toAgentId, note) => action('transferConversation', { id, toAgentId, note }),
  changeOwner: (userId, agentId, convId) => action('changeCustomerOwner', { userId, agentId, convId }),   // 只有管理员
  close: (id) => action('closeConversation', { id }),
  setOutcome: (id, outcome, outcomeDate, amount, historical) => action('setConversationOutcome', { id, outcome, outcomeDate, amount, historical }),
  getOrderSyncStatus: () => action('getOrderSyncStatus'),
  getOrderSyncSetup: (p = {}) => action('getOrderSyncSetup', p),
  retryOrderSync: (id) => action('retryOrderSync', { id }),
  retryOrderSyncJob: async (jobId) => {
    const result = await action('retryOrderSync', { jobId })
    // request() historically maps unreadable JSON to {}. A missing explicit
    // acknowledgement is an uncertain write, not a business rejection: stop a
    // batch rather than continuing after an interrupted/truncated 200 response.
    if (result?.ok !== true && result?.ok !== false) {
      throw new TypeError('重试提交结果无法确认，请刷新核实后再操作')
    }
    return result
  },
  cancelOrderVerification: (id) => action('cancelOrderVerification', { id }),
  voidOrderVerification: (id) => action('voidOrderVerification', { id }),
  resolveOrderIdentity: (p) => action('resolveOrderIdentity', p),
  clearRiskFlag: (id, label) => action('clearRiskFlag', { id, label }),
  clearPaymentVerification: (id) => action('clearPaymentVerification', { id }),
  getReport: (p) => action('getReport', p),   // 数据统计页（进线按实际进线日，成交/失败按标记日）
  getCwtOperatorStats: (agentId) => action('getCwtOperatorStats', { agentId }),
  // 车网通同期收款（按运营）：统计页的「成交」以它为准
  getCwtPaid: (p = {}) => action('getCwtPaid', p),
  getMonitor: () => action('getMonitor'),
  // 会话记录：按客户聚合（一个车牌/手机号一行，不管进线过几次）
  listCustomerHistory: (p) => action('listCustomerHistory', p),
  listCustomerConversations: (userIds, orderId) => action('listCustomerConversations', { userIds, orderId }),
  sendAgentMessage: (convId, content, agentId, quoteMsgId) => action('sendAgentMessage', { convId, content, agentId, quoteMsgId }),
  sendVoice: (convId, dataUrl) => action('sendVoiceToCustomer', { convId, dataUrl }),
  sendImage: (convId, dataUrl) => action('sendImageToCustomer', { convId, dataUrl }),
  recallMessage: (convId, msgId) => action('recallMessage', { convId, msgId }),
  // 账号
  listAgents: () => action('listAgents'),
  getMyPet: () => action('getMyPet'),
  getSendQuota: (convId) => action('getSendQuota', { convId }),
  getLiveCode: () => action('getLiveCode'),
  setLiveCode: (url, note) => action('setLiveCode', { url, note }),
  updateMyPet: (p) => action('updateMyPet', p),
  drawNewbiePet: () => action('drawNewbiePet'),
  getPetRules: () => action('getPetRules'),
  setPetRules: (rules) => action('setPetRules', { rules }),
  getFreeApply: (p) => action('getFreeApply', p),
  setFreeApply: (config) => action('setFreeApply', { config }),
  markFreeApplyContacted: (id, contacted) => action('markFreeApplyContacted', { id, contacted }),
  createAgent: (p) => action('createAgent', p),
  updateAgent: (p) => action('updateAgent', p),
  deleteAgent: (id) => action('deleteAgent', { id }),
  setOnline: (agentId, online) => action('setAgentOnline', { agentId, online }),
  setAccepting: (agentId, accepting) => action('setAgentAccepting', { agentId, accepting }),
  setAgentActive: (agentId, active) => action('setAgentActive', { agentId, active }),
  // 客户
  renameCustomer: (userId, nickname) => action('renameCustomer', { userId, nickname }),
  resetCustomerName: (userId) => action('resetCustomerName', { userId }),
  // 每日签收派单控制：后端未部署时会按普通 API 错误返回，设置页负责给出可读错误。
  getSignedDispatchStatus: (p = {}) => action('getSignedDispatchStatus', p),
  getSignedDispatchPreview: (p = {}) => action('getSignedDispatchPreview', p),
  setSignedDispatchDailyControl: (p) => action('setSignedDispatchDailyControl', p),
  reconcileSignedDispatchPreview: (p = {}) => action('reconcileSignedDispatchPreview', p),
  reconcileSignedDispatchCommit: (p = {}) => action('reconcileSignedDispatchCommit', p),
  clearSignedDispatchDailyControl: (p) => action('clearSignedDispatchDailyControl', p),
  // 排班表（自动派单据此判断谁在班）
  listShifts: (p) => action('listShifts', p || {}),
  importShifts: (rows) => action('importShifts', { rows }),
  // 渠道
  listChannels: () => action('listChannels'),
  updateChannel: (p) => action('updateChannel', p),
  // 快捷话术
  listQuickReplies: () => action('listQuickReplies'),
  upsertQuickReply: (reply) => action('upsertQuickReply', { reply }),
  deleteQuickReply: (id) => action('deleteQuickReply', { id }),
  useQuickReply: (id) => action('useQuickReply', { id }),
  // 知识库
  listFaqs: (p) => action('listFaqs', p),
  listKbDocs: (p) => action('listKbDocs', p),
  getKbDoc: (id) => action('getKbDoc', { id }),
  updateKbDoc: (id, patch) => action('updateKbDoc', { id, patch }),
  getSkill: () => action('getSkill'),
  getSkillSettings: () => action('getSkillSettings'),
  saveSkillSettings: (p) => action('saveSkillSettings', p),
  saveSkill: (content) => action('saveSkill', { content }),
  upsertFaq: (faq) => action('upsertFaq', { faq }),
  deleteFaq: (id) => action('deleteFaq', { id }),
  listMedia: () => action('listMedia'),
  deleteMedia: (id) => action('deleteMedia', { id }),
  updateMedia: (p) => action('updateMedia', p),
  sendMedia: (convId, mediaId) => action('sendMediaToCustomer', { convId, mediaId }),
  // 会话媒体走鉴权接口，<img src> / <audio src> 带不了 Authorization 头，
  // 只能先带令牌取回来再转成本地 blob 地址给标签用。用完记得 revoke，否则内存泄漏。
  mediaObjectUrl: async (file) => {
    const res = await fetch(`/api/cs/media/file/${encodeURIComponent(file)}`, {
      headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {},
    })
    if (!res.ok) {
      const d = await res.json().catch(() => ({}))
      throw new Error(d.error || '附件加载失败')
    }
    return URL.createObjectURL(await res.blob())
  },
  uploadMedia: ({ file, title, category }, onProgress) => {
    const info = libraryFileType(file)
    return uploadBinary('/api/cs/media/upload', { title, category, filename: file.name }, file, info.mime, onProgress)
  },
  sendFile: (convId, file, onProgress) => {
    const info = documentType(file)
    return uploadBinary('/api/cs/media/send-file', { convId, filename: file.name }, file, info.mime, onProgress)
  },
}

// Raw binary keeps a 20MB document within the existing proxy limit, with upload progress.
function uploadBinary(path, params, file, mime, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${path}?${new URLSearchParams(params)}`)
    xhr.setRequestHeader('Content-Type', mime)
    xhr.setRequestHeader('Authorization', `Bearer ${auth.token}`)
    xhr.upload.onprogress = e => { if (e.lengthComputable) onProgress?.(Math.floor(e.loaded / e.total * 100)) }
    xhr.onload = () => {
      let d = {}
      try { d = JSON.parse(xhr.responseText) } catch {}
      if (xhr.status >= 200 && xhr.status < 300) resolve(d.data)
      else {
        if (xhr.status === 401) {
          auth.clear()
          if (!location.hash.includes('/login')) location.hash = '#/login'
        }
        reject(new ApiError(d.error || (xhr.status === 413 ? '文件太大' : `上传失败（${xhr.status}）`), { status: xhr.status, code: d.code }))
      }
    }
    // No automatic retry: the upstream may already have accepted a send.
    xhr.onerror = () => reject(new Error('网络中断，请核对会话发送记录后再重试'))
    xhr.onabort = () => reject(new Error('上传已取消'))
    xhr.send(file)
  })
}
